import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import ValidatorUtils from '@core/utils/validator.utils';
import {
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvNumberboxElement,
  DvRadioGroupLegacyElement,
  DvSpaceElement,
  DvTextareaElement,
  DvTextboxElement,
} from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import { TripType } from '@pages/trips-page/constants';
import { Country } from '@shared/constants/country';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { dvNumberMaskPresets } from '@devinco/ui/dds';

@Injectable()
export class TripInformationTabService {
  private readonly restApi = inject(RestApiService);
  private translate = inject(TranslateService);
  private userMetadataService = inject(UserMetadataService);

  readonly driverElements = [
    new DvDropdownClientSideElement({
      colspan: 6,
      key: 'UserID',
      labelKeys: ['UserNameDescription'],
      label: 'TripInformationTab.Driver',
      valueKey: 'UserID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.User,
          fields: ['UserID', 'UserNameDescription'],
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvSpaceElement({ colspan: 6 }),
  ];

  readonly tripDetailsElements = [
    new DvTextboxElement({
      colspan: 12,
      key: 'StartLocation',
      label: 'TripInformationTab.StartLocation',
    }),
    new DvTextboxElement({
      colspan: 12,
      key: 'EndLocation',
      label: 'TripInformationTab.EndLocation',
    }),
  ];

  readonly purposeElements = [
    new DvTextareaElement({
      colspan: 12,
      key: 'Purpose',
      label: 'TripInformationTab.Purpose',
      rowspan: 2,
      validators: [Validators.maxLength(400)],
    }),
  ];

  readonly purposeRequiredHint = this.translate.instant(
    'TripInformationTab.PurposeRequiredForCorporateTrips'
  );

  readonly personalVehicleTypeRadioItems = [
    {
      value: TripType.Personal,
      label: this.translate.instant('TripInformationTab.PersonalTripType'),
    },
    {
      value: TripType.Corporate,
      label: this.translate.instant(
        'TripInformationTab.BusinessCorporateTripType'
      ),
    },
  ];

  readonly invisibleElements: DvDynamicFormElement[] = [
    new DvTextboxElement({ key: 'VehicleType' }),
    new DvTextboxElement({ key: 'VehicleTypeIsPersonal' }),
    new DvTextboxElement({ key: 'PrivateOccasionalDistance' }),
    new DvTextboxElement({ key: 'PrivateOccasionalNotAllowed' }),
    new DvTextboxElement({ key: 'PrivateOccasionalCount' }),
    new DvTextboxElement({ key: 'Distance' }),
    new DvTextboxElement({ key: 'StartGPSLat' }),
    new DvTextboxElement({ key: 'StartGPSLon' }),
    new DvTextboxElement({ key: 'EndGPSLat' }),
    new DvTextboxElement({ key: 'EndGPSLon' }),
    new DvTextboxElement({ key: 'IsPrivateTaxable' }),
  ];

  readonly allElements = [
    ...this.driverElements,
    ...this.tripDetailsElements,
    ...this.purposeElements,
    ...this.invisibleElements,
    ...this.getTripTypeElements(),
  ];

  getCorporateVehicleTypeRadioItems(
    privateTaxableNotAllowed: boolean
  ): { value: TripType; label: string }[] {
    const response = [
      {
        value: TripType.Corporate,
        label: this.translate.instant(
          'TripInformationTab.BusinessCorporateTripType'
        ),
      },
      {
        value: TripType.PrivateOccasional,
        label: this.translate.instant(
          'TripInformationTab.PrivateOccasionalTripType'
        ),
      },
    ];

    if (
      this.userMetadataService.getUserCountry() === Country.Norway &&
      !privateTaxableNotAllowed
    ) {
      response.push(response[1]);
      response[1] = {
        value: TripType.PrivateTaxable,
        label: this.translate.instant(
          'TripInformationTab.PrivateTaxableTripType'
        ),
      };
    }

    return response;
  }

  getTripTypeElements(): DvDynamicFormElement[] {
    const elements = [
      new DvRadioGroupLegacyElement({
        colspan: 12,
        key: 'TripType',
        direction: 'vertical',
        labelKey: 'label',
        valueKey: 'value',
        items: ['on'],
        gridSize: 1,
      }),
      new DvNumberboxElement({
        colspan: 6,
        key: 'DistancePrivateTaxable',
        label: 'TripInformationTab.Distance',
        fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
        validators: [
          Validators.required,
          Validators.min(0.1),
          ValidatorUtils.lowerThanOtherControlValidator('Distance'),
        ],
      }),
    ];

    return elements;
  }
}
